import { Component, OnInit, HostListener, ViewChild, CUSTOM_ELEMENTS_SCHEMA, NO_ERRORS_SCHEMA } from "@angular/core";
import { CommonModule } from "@angular/common";
import { FormsModule, ReactiveFormsModule } from "@angular/forms";
import { RouterModule } from "@angular/router";
import { Router, ActivatedRoute } from "@angular/router";
import { ToastrService } from "ngx-toastr";
import { FormControl, FormGroup, Validators } from "@angular/forms";
import { ICountry } from "../../interfaces/icountry";
import { IEvent } from "../../interfaces/ievent";
import { IBusinessFunction } from "../../interfaces/ibusinessfunction";
import { DropDownItem } from "../../models/dropdown";
import { EventService } from "../../services/event.service";
import { CountryService } from "../../services/country.service";
import { BusinessFunctionService } from "../../services/businessfunction.service";
import { EventLatestUpdate } from "../../models/latestupdate";
import { LatestUpdateService } from "../../services/latestupdate.service";
import { TrimmedMinLengthValidator } from "../../validators/trimmedMinLength.validator";
import { ILatestUpdateModal } from "../../interfaces/ilatestupdatemodal";
import { IAppStore } from "../../iappstore";
import { Store } from "@ngrx/store";
import { AppSettings } from "../../appsettings";
import { ICanComponentDeactivate } from "../../interfaces/icancomponentdeactivate";
import { Observable } from "rxjs";
import { CKEditorModule } from "@ckeditor/ckeditor5-angular";
import * as ClassicEditor from '@ckeditor/ckeditor5-build-classic';
import { CkEditorEnhancedDirective } from "../../directives/ck-editor-enhanced.directive";
import { NgSelectComponent } from "../shared/ng-select/ng-select.component";
import { ConfirmationModalComponent } from "../confirmationmodal/confirmationmodal.component";
import { LatestUpdateModalComponent } from "../latestupdatemodal/latestupdatemodal.component";
import { HeaderComponent } from "../header/header.component";

/**
 * Event component
 */
@Component({
    selector: "event",
    templateUrl: "./event.component.html",
    standalone: true,
    imports: [CommonModule, FormsModule, ReactiveFormsModule, RouterModule, CKEditorModule, CkEditorEnhancedDirective, NgSelectComponent, ConfirmationModalComponent, LatestUpdateModalComponent, HeaderComponent],
    schemas: [CUSTOM_ELEMENTS_SCHEMA, NO_ERRORS_SCHEMA]
})
export class EventComponent implements OnInit, ICanComponentDeactivate {
    public Editor = ClassicEditor;
    countries: DropDownItem[] = new Array();
    allCountries: ICountry[] = new Array();
    selectedCountry: DropDownItem[] = new Array();
    countryNotSelected: boolean = false;
    eventForm: FormGroup;
    id: number = null;
    updateText: string = "";
    businessFunction: IBusinessFunction;
    textEditorConfig: {};
    title = "";
    @ViewChild("confirmModal") confirmModal;

    constructor(private toastr: ToastrService,
        private eventService: EventService,
        private countryService: CountryService,
        private businessFunctionService: BusinessFunctionService,
        private latestUpdateService: LatestUpdateService,
        private activatedRoute: ActivatedRoute,
        private router: Router,
        store: Store<IAppStore>) {
        store.select(e => e.user.selectedBusinessFunction)
            .subscribe(e => this.businessFunction = e);
    }

    /**
     * On initailse of an event component.
     * Here it initailse the form contents and load the data to display.
     */
    ngOnInit(): void {
        this.textEditorConfig = AppSettings.ckeditorConfig;
        let countriesData: ICountry[] = this.activatedRoute.snapshot.data["countries"];
        this.countries = countriesData.map((e: ICountry) => new DropDownItem(e.id, e.name, null));
        countriesData.map((e: ICountry) => {
            this.allCountries.push(e);
        });

        let text: string = "";
        let publishDate: string = new Date().toISOString().substring(0, 10);
        let url: string = null;
        let countryId: number = null;
        let disabled: boolean = false;
        if (this.activatedRoute.snapshot.data != null && this.activatedRoute.snapshot.data["event"] != null) {
            let eventData: any = this.activatedRoute.snapshot.data["event"];
            this.id = eventData.id;
            text = eventData.text;
            let date = new Date(eventData.publishDate);
            date.setDate(date.getDate() + 1);
            publishDate = date.toISOString().substring(0, 10);
            url = eventData.url;
            countryId = eventData.country.id;
            disabled = eventData.disabled;
            this.getSelectedCountryDropdown(countryId);
        }

        this.eventForm = new FormGroup({
            text: new FormControl(text, [Validators.required, TrimmedMinLengthValidator(3)]),
            publishDate: new FormControl(publishDate, [Validators.required]),
            url: new FormControl(url,
                [
                    Validators.pattern(AppSettings.urlRegExp.source)
                ]),
            countryId: new FormControl(countryId, [Validators.required]),
            disabled: new FormControl(disabled)
        });
        this.eventForm.controls["text"].valueChanges.subscribe(h => this.updateText = h);
        this.setLatestUpdateContent();

        this.title = this.id ? "Update event" : "Create event";
    }

    /**
     * Set the the selected country value
     * @param countryId The country id which needs to selected.
     */
    getSelectedCountryDropdown(countryId: number): void {
        let matchingCountries: ICountry[] = this.allCountries.filter(c => c.id === countryId);
        if (matchingCountries != null && matchingCountries.length > 0) {
            this.selectedCountry.push({ id: matchingCountries[0].id, text: matchingCountries[0].name, code: null });
        }
    }

    /**
     * Get the the selected country Code
     */
    getCountryCode(): string {
        let countryCode: string = "";
        let countryId: number = this.eventForm.controls["countryId"].value;
        const matchingCountries: ICountry[] = this.allCountries.filter(c => c.id === countryId);
        if (matchingCountries != null && matchingCountries.length > 0) {
            countryCode = matchingCountries[0].code;
        }
        return countryCode;
    }

    /**
     * Fires on selection of a country dropdown
     * @param event The selected country value.
     */
    countrySelected(event: any): void {
        (<FormControl>this.eventForm.controls["countryId"])
            .setValue(event.id, { onlySelf: false });
    }

    /**
     * Saves updated event data or creates a new event.
     * @param event An event data to create or update.
     * @param valid A boolean value indicates if the form is valid or not.
     */
    saveTimelineEvent(event: IEvent, valid: boolean, redirect: boolean): void {
        if (valid) {
            this.countryNotSelected = false;
            event.businessFunctionId = this.businessFunction.id;
            event.country = { id: event.countryId, shortName: "", code: "", isoCode: "", name: "" };
            if (this.id == null) {
                this.eventService.create(event).subscribe(t => {
                    this.id = t.id;
                    this.toastr.success("Event created");
                    if (redirect) {
                        this.eventForm.reset();
                        this.router.navigate(["./timeline"]);
                    }
                });
            } else {
                event.id = this.id;
                event.businessFunctionId = this.businessFunction.id;
                event.country = { id: event.countryId, shortName: "", code: "", isoCode: "", name: "" };
                // update the data here.
                this.eventService.update(event).subscribe(() => {
                    this.toastr.success("Event updated");
                    if (redirect) {
                        this.eventForm.reset();
                        this.router.navigate(["./timeline"]);
                    }
                });
            }
        } else {
            this.countryNotSelected = this.eventForm.controls["countryId"].hasError("required");
            this.toastr.error("Please correct errors before continuing");
        }
    }

    /**
     * Sets the latest update text
     */
    setLatestUpdateContent(): void {
        let headlineText: string = this.getHeadlineText();
        this.updateText = headlineText;
    }

    /**
     * Get headline text
     */
    getHeadlineText(): string {
        let headlineText: string = "";
        if (this.eventForm.controls["text"].value != null) {
            headlineText = this.eventForm.controls["text"].value;
        }

        return headlineText;
    }

    /**
     * Publishes the content for latest updates
     */
    publishContent(latestUpdateContent: ILatestUpdateModal): void {
        // if we use a modal, move this logic
        if (latestUpdateContent.latestUpdateAlert) {
            const eventLatestUpdate: EventLatestUpdate = new EventLatestUpdate(this.eventForm.controls["countryId"].value,
                this.businessFunction.id, latestUpdateContent.description,
                this.getCountryCode(),
                this.eventForm.controls["url"].value,
                this.businessFunction.defaultRoute,
                latestUpdateContent.sendEmail);
            this.latestUpdateService.create(eventLatestUpdate).subscribe(() => {
                this.eventForm.reset();
                this.toastr.success("Latest update published");
                this.eventForm.reset();
                this.router.navigate(["./timeline"]);
            });
        } else {
            this.eventForm.reset();
            this.router.navigate(["./timeline"]);
        }
    }

    @HostListener("window:beforeunload")
    canDeactivate(): Observable<boolean> | Promise<boolean> | boolean {
        if (this.eventForm.dirty) {
            this.confirmModal.openModal();
            return false;
        } else {
            return true;
        }
    }
    onAccept(): void {
        // reset the the original back to whats currenlty in itemConfigSet, this allows to pass check  in candeactivate
        this.eventForm.reset();
        this.router.navigate([this.navigatingTo]);
        this.confirmModal.closeModal();
    }

    onDecline(): void {
        this.confirmModal.closeModal();
    }
    navigatingTo: string;
}


